// components/admin/ReviewManagement.tsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  Chip,
  Rating,
  FormHelperText,
  Badge,
  Menu,
  Grid,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Delete,
  Edit,
  Search,
  FilterList,
  ArrowBack,
  Check,
  Pending,
  MoreVert,
  ThumbUp,
  ThumbDown,
  Block,
  ShoppingBag,
  Store,
  Comment as CommentIcon,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListReviewsQuery,
  useDeleteReviewMutation,
  useGetReviewCountQuery,
  useAddReviewMutation,
  useUpdateReviewMutation,
  Review,
  ReviewFormData,
  FormErrors,
  SnackbarState,
} from '../../features/admin/reviewApi.ts';

const SEARCH_PARAM = 'search_str';

const ReviewManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Type tab: 0=All, 1=Product Reviews (type=0), 2=Seller Reviews (type=1)
  const [typeTabValue, setTypeTabValue] = useState(0);
  // Status tab: 0=All, 1=Pending, 2=Approved, 3=Rejected
  const [statusTabValue, setStatusTabValue] = useState(0);

  const [productIdFilter, setProductIdFilter] = useState<string>('');
  const [sellerIdFilter, setSellerIdFilter] = useState<string>('');
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Form related states
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [formData, setFormData] = useState<ReviewFormData>({
    product_id: 0,
    seller_id: null,
    rating: 0,
    comment: '',
    status: 0,
    type: 0,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  // Quick action states
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  // Ref for scrolling - SINGLE SCROLLBAR
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const reviewsPerPage = 10;

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchStr);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchStr]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
    // Reset scroll position when page changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [debouncedSearch, typeTabValue, statusTabValue, productIdFilter, sellerIdFilter]);

  // Build query params
  const queryParams = useMemo(() => {
    const params: any = {
      start: (page - 1) * reviewsPerPage,
      limit: reviewsPerPage,
    };

    if (debouncedSearch?.trim()) {
      params[SEARCH_PARAM] = debouncedSearch.trim();
    }

    // Status filter
    if (statusTabValue === 1) {
      params.status = 0;  // Pending
    } else if (statusTabValue === 2) {
      params.status = 1;  // Approved
    } else if (statusTabValue === 3) {
      params.status = 2;  // Rejected
    }

    // Type filter
    if (typeTabValue === 1) {
      params.type = 0;  // Product Reviews
    } else if (typeTabValue === 2) {
      params.type = 1;  // Seller Reviews
    }

    if (productIdFilter && parseInt(productIdFilter) > 0) {
      params.product_id = parseInt(productIdFilter);
    }

    if (sellerIdFilter && parseInt(sellerIdFilter) > 0) {
      params.seller_id = parseInt(sellerIdFilter);
    }

    return params;
  }, [page, debouncedSearch, typeTabValue, statusTabValue, productIdFilter, sellerIdFilter]);

  // Count params for different tabs
  const allCountParams = useMemo(() => {
    const { start, limit, ...rest } = queryParams;
    return rest;
  }, [queryParams]);

  const pendingCountParams = useMemo(() => ({ status: 0 }), []);
  const approvedCountParams = useMemo(() => ({ status: 1 }), []);
  const rejectedCountParams = useMemo(() => ({ status: 2 }), []);
  const productCountParams = useMemo(() => ({ type: 0 }), []);
  const sellerCountParams = useMemo(() => ({ type: 1 }), []);

  // API hooks
  const {
    data: reviewsResponse,
    isLoading,
    error,
    isError,
    refetch
  } = useListReviewsQuery(queryParams);

  const { data: allCountResponse } = useGetReviewCountQuery(allCountParams);
  const { data: pendingCountResponse } = useGetReviewCountQuery(pendingCountParams);
  const { data: approvedCountResponse } = useGetReviewCountQuery(approvedCountParams);
  const { data: rejectedCountResponse } = useGetReviewCountQuery(rejectedCountParams);
  const { data: productCountResponse } = useGetReviewCountQuery(productCountParams);
  const { data: sellerCountResponse } = useGetReviewCountQuery(sellerCountParams);

  const [deleteReview] = useDeleteReviewMutation();
  const [addReview] = useAddReviewMutation();
  const [updateReview] = useUpdateReviewMutation();

  // Derived values
  const reviews = reviewsResponse?.result || reviewsResponse?.data || [];
  const totalRecords = allCountResponse?.count || 0;
  const totalPages = Math.ceil(totalRecords / reviewsPerPage);
  
  const pendingCount = pendingCountResponse?.count || 0;
  const approvedCount = approvedCountResponse?.count || 0;
  const rejectedCount = rejectedCountResponse?.count || 0;
  const productReviewCount = productCountResponse?.count || 0;
  const sellerReviewCount = sellerCountResponse?.count || 0;

  const isEditing = Boolean(editingReview);

  // Scroll handlers
  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Handlers
  const handleChangePage = useCallback((_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    // Reset scroll position when page changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, []);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(e.target.value);
  }, []);

  const handleProductIdFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setProductIdFilter(e.target.value);
  }, []);

  const handleSellerIdFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSellerIdFilter(e.target.value);
  }, []);

  const handleTypeTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setTypeTabValue(newValue);
  }, []);

  const handleStatusTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setStatusTabValue(newValue);
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({ product_id: 0, seller_id: null, rating: 0, comment: '', status: 0, type: 0 });
    setFormErrors({});
    setEditingReview(null);
  }, []);

  const handleAddReview = useCallback(() => {
    resetForm();
    setShowForm(true);
  }, [resetForm]);

  const handleEditReview = useCallback((review: Review) => {
    setFormData({
      product_id: review.product_id,
      seller_id: review.seller_id,
      rating: review.rating,
      comment: review.comment,
      status: review.status,
      type: review.type ?? (review.seller_id && review.seller_id > 0 ? 1 : 0),
    });
    setEditingReview(review);
    setShowForm(true);
  }, []);

  const handleBackToList = useCallback(() => {
    setShowForm(false);
    resetForm();
  }, [resetForm]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'seller_id') {
        const numVal = value === '' ? null : Number(value);
        return { ...prev, seller_id: numVal, type: value === '' ? 0 : 1 };
      }
      if (['product_id', 'rating', 'status'].includes(name)) {
        return { ...prev, [name]: value === '' ? 0 : Number(value) };
      }
      return { ...prev, [name]: value };
    });
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  }, [formErrors]);

  const handleStatusChange = useCallback((e: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, status: Number(e.target.value) }));
  }, []);

  const handleRatingChange = useCallback((_: React.SyntheticEvent, newValue: number | null) => {
    if (newValue !== null) setFormData(prev => ({ ...prev, rating: newValue }));
    if (formErrors.rating) setFormErrors(prev => ({ ...prev, rating: '' }));
  }, [formErrors]);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};
    if (!isEditing) {
      if (!formData.product_id || formData.product_id <= 0) newErrors.product_id = 'Product ID is required';
      if (formData.rating <= 0 || formData.rating > 5) newErrors.rating = 'Rating must be between 1 and 5';
      if (!formData.comment?.trim()) newErrors.comment = 'Comment is required';
    }
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isEditing]);

  const refetchAll = useCallback(() => {
    return Promise.all([refetch()]);
  }, [refetch]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsFormSubmitting(true);
    try {
      let result;
      if (isEditing && editingReview) {
        result = await updateReview({ review_id: editingReview.id, status: formData.status }).unwrap();
        setSnackbar({ open: true, message: result.message || 'Review updated successfully!', severity: 'success' });
      } else {
        result = await addReview({
          product_id: formData.product_id,
          seller_id: formData.seller_id,
          rating: formData.rating,
          comment: formData.comment,
          type: formData.seller_id ? 1 : 0,
        }).unwrap();
        setSnackbar({ open: true, message: result.message || 'Review added successfully!', severity: 'success' });
      }
      handleBackToList();
      await refetchAll();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.data?.message || err.message || `Error ${isEditing ? 'updating' : 'adding'} review.`,
        severity: 'error',
      });
    } finally {
      setIsFormSubmitting(false);
    }
  }, [validateForm, isEditing, editingReview, formData, updateReview, addReview, handleBackToList, refetchAll]);

  const handleDelete = useCallback(async (reviewId: number) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      const result = await deleteReview({ review_id: reviewId }).unwrap();
      setSnackbar({ open: true, message: result.message || 'Review deleted successfully!', severity: 'success' });
      await refetchAll();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.data?.message || 'Error deleting review.', severity: 'error' });
    }
  }, [deleteReview, refetchAll]);

  const handleQuickStatusUpdate = useCallback(async (review: Review, newStatus: number) => {
    setUpdatingStatus(review.id);
    try {
      const result = await updateReview({ review_id: review.id, status: newStatus }).unwrap();
      const statusLabel = ['marked as pending', 'approved', 'rejected'][newStatus] ?? 'updated';
      setSnackbar({ open: true, message: result.message || `Review ${statusLabel} successfully!`, severity: 'success' });
      setActionMenuAnchor(null);
      setSelectedReviewId(null);
      await refetchAll();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.data?.message || 'Error updating review status.', severity: 'error' });
    } finally {
      setUpdatingStatus(null);
    }
  }, [updateReview, refetchAll]);

  const handleActionMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, reviewId: number) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedReviewId(reviewId);
  }, []);

  const handleActionMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedReviewId(null);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchStr('');
    setProductIdFilter('');
    setSellerIdFilter('');
    setTypeTabValue(0);
    setStatusTabValue(0);
    setPage(1);
  }, []);

  // Render helpers
  const renderStatus = useCallback((status: number) => {
    const map: Record<number, { label: string; bg: string; color: string }> = {
      0: { label: 'Pending', bg: '#fff3e0', color: '#e65100' },
      1: { label: 'Approved', bg: '#d1f4e0', color: '#00783e' },
      2: { label: 'Rejected', bg: '#ffebee', color: '#c62828' },
    };
    const s = map[status] ?? map[0];
    return <Chip label={s.label} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, fontSize: '12px' }} />;
  }, []);

  const renderReviewType = useCallback((review: Review) => {
    const isSeller = review.type === 1 || (review.seller_id != null && review.seller_id > 0);
    return isSeller
      ? <Chip label="Seller Review" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '11px' }} />
      : <Chip label="Product Review" size="small" sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 600, fontSize: '11px' }} />;
  }, []);

  const getAvailableStatusOptions = useCallback((currentStatus: number) => {
    return [
      { value: 0, label: 'Pending', icon: <Pending />, color: '#e65100' },
      { value: 1, label: 'Approve', icon: <ThumbUp />, color: '#00783e' },
      { value: 2, label: 'Reject', icon: <Block />, color: '#c62828' },
    ].filter(s => s.value !== currentStatus);
  }, []);

  const formatDate = useCallback((dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateString; }
  }, []);

  const tabSx = {
    '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 44, color: '#6d7175' },
    '& .Mui-selected': { color: '#202223', fontWeight: 600 },
    '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} pendingReviewCount={pendingCount} rejectedReviewCount={rejectedCount} />
        </Box>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} pendingReviewCount={pendingCount} rejectedReviewCount={rejectedCount} />
        </Drawer>

        {/* Main content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            {!showForm ? (
              // LIST VIEW
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
                {/* Page header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Reviews</Typography>
                  <Button
                    variant="contained"
                    onClick={handleAddReview}
                    sx={{ bgcolor: '#202223', color: 'white', textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#303030' } }}
                  >
                    Add review
                  </Button>
                </Box>

                {/* Status Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={statusTabValue} onChange={handleStatusTabChange} sx={tabSx}>
                    <Tab label={`All (${totalRecords})`} />
                    <Tab label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Pending
                        <Badge badgeContent={pendingCount} color="warning" sx={{ ml: 1, '& .MuiBadge-badge': { fontSize: '10px', height: 18, minWidth: 18 } }} />
                      </Box>
                    } />
                    <Tab label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Approved
                        <Badge badgeContent={approvedCount} color="success" sx={{ ml: 1, '& .MuiBadge-badge': { fontSize: '10px', height: 18,  minWidth: 18 } }} />
                      </Box>
                    } />
                    <Tab label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Rejected
                        <Badge badgeContent={rejectedCount} color="error" sx={{ ml: 1, '& .MuiBadge-badge': { fontSize: '10px', height: 18, minWidth: 18 } }} />
                      </Box>
                    } />
                  </Tabs>
                </Box>

                {/* Type Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={typeTabValue} onChange={handleTypeTabChange} sx={tabSx}>
                    <Tab label={`All (${totalRecords})`} />
                    <Tab label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShoppingBag sx={{ mr: 1, fontSize: 18 }} />
                        Product Reviews
                        <Badge badgeContent={productReviewCount} color="primary" sx={{ ml: 1, '& .MuiBadge-badge': { fontSize: '10px', height: 18, minWidth: 18 } }} />
                      </Box>
                    } />
                    <Tab label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Store sx={{ mr: 1, fontSize: 18 }} />
                        Seller Reviews
                        <Badge badgeContent={sellerReviewCount} color="secondary" sx={{ ml: 1, '& .MuiBadge-badge': { fontSize: '10px', height: 18, minWidth: 18 } }} />
                      </Box>
                    } />
                  </Tabs>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  {/* Filters */}
                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Search by customer name or comment..."
                      value={searchStr}
                      onChange={handleSearch}
                      sx={{ minWidth: 250, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {typeTabValue !== 2 && (
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Filter by Product ID"
                        value={productIdFilter}
                        onChange={handleProductIdFilterChange}
                        sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                        InputProps={{ inputProps: { min: 1 } }}
                      />
                    )}

                    {typeTabValue !== 1 && (
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Filter by Seller ID"
                        value={sellerIdFilter}
                        onChange={handleSellerIdFilterChange}
                        sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                        InputProps={{ inputProps: { min: 1 } }}
                      />
                    )}

                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={clearFilters}
                      sx={{ textTransform: 'none', borderRadius: '8px', borderColor: '#e1e3e5', color: '#202223', '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}
                    >
                      Clear filters
                    </Button>

                    {!isLoading && (
                      <Typography variant="body2" sx={{ ml: 'auto', color: '#6d7175', fontSize: '13px' }}>
                        {reviews.length} review{reviews.length !== 1 ? 's' : ''} (current page)
                      </Typography>
                    )}
                  </Box>

                  {/* Loading/Error/Empty states */}
                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">
                        Failed to load reviews. {error && 'data' in error && error.data && typeof error.data === 'object' && 'message' in error.data 
                          ? (error.data as any).message 
                          : 'Please try again.'}
                      </Alert>
                    </Box>
                  )}

                  {!isLoading && reviews.length === 0 && (
                    <Box sx={{ p: 5, textAlign: 'center', bgcolor: 'white' }}>
                      <Typography color="text.secondary" sx={{ fontSize: '15px' }}>
                        No reviews found.
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Try changing the filter or search criteria.
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && reviews.length > 0 && (
                    <>
                      {/* Scroll Navigation Bar */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb',
                      }}>
                        <IconButton
                          size="small"
                          onClick={handleScrollLeft}
                          sx={{
                            color: '#6d7175',
                            border: '1px solid #e1e3e5',
                            borderRadius: '4px',
                            width: 28,
                            height: 28,
                            '&:hover': { bgcolor: '#e1e3e5', color: '#202223' }
                          }}
                        >
                          <ChevronLeft fontSize="small" />
                        </IconButton>

                        <Box sx={{
                          flex: 1,
                          height: '4px',
                          mx: 1,
                          bgcolor: '#e1e3e5',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}>
                          <Box sx={{
                            height: '100%',
                            width: '30%',
                            bgcolor: '#6d7175',
                            borderRadius: '2px',
                            opacity: 0.4,
                          }} />
                        </Box>

                        <IconButton
                          size="small"
                          onClick={handleScrollRight}
                          sx={{
                            color: '#6d7175',
                            border: '1px solid #e1e3e5',
                            borderRadius: '4px',
                            width: 28,
                            height: 28,
                            '&:hover': { bgcolor: '#e1e3e5', color: '#202223' }
                          }}
                        >
                          <ChevronRight fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Scrollable Table Container */}
                      <Box
                        ref={scrollContainerRef}
                        sx={{
                          overflowX: 'auto',
                          bgcolor: 'white',
                          '&::-webkit-scrollbar': {
                            height: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: '#f1f2f4',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: '#c1c4c8',
                            borderRadius: '4px',
                            '&:hover': {
                              background: '#a1a4a8',
                            },
                          },
                        }}
                      >
                        {/* Table Header */}
                        <Box sx={{
                          display: 'grid',
                          gridTemplateColumns: '50px 150px 200px 100px 100px 100px 150px 180px 110px 180px 100px',
                          gap: 2,
                          px: 2,
                          py: 1.5,
                          borderBottom: '1px solid #e1e3e5',
                          bgcolor: '#fafbfb',
                          minWidth: '1400px',
                        }}>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            #
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Customer
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Comment
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Type
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Product ID
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Seller ID
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Rating
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Date
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Status
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Quick Actions
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                            Actions
                          </Typography>
                        </Box>

                        {/* Table Rows */}
                        {reviews.map((review: Review, index: number) => {
                          const reviewId = review.id;
                          const availableStatuses = getAvailableStatusOptions(review.status);

                          return (
                            <Box
                              key={reviewId}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '50px 150px 200px 100px 100px 100px 150px 180px 110px 180px 100px',
                                gap: 2,
                                px: 2,
                                py: 2,
                                alignItems: 'center',
                                borderBottom: index < reviews.length - 1 ? '1px solid #e1e3e5' : 'none',
                                '&:hover': { bgcolor: '#f9fafb' },
                                minWidth: '1400px',
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#6d7175' }}>
                                {(page - 1) * reviewsPerPage + index + 1}
                              </Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#f1f2f4', fontSize: '14px' }}>
                                  {(review.customer_name || 'A')[0].toUpperCase()}
                                </Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                  {review.customer_name || 'Anonymous'}
                                </Typography>
                              </Box>

                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px', maxWidth: '200px', wordBreak: 'break-word' }}>
                                {review.comment}
                              </Typography>

                              <Box>{renderReviewType(review)}</Box>

                              <Typography variant="body2" sx={{ color: '#202223', fontWeight: 500 }}>
                                {review.product_id}
                              </Typography>

                              <Typography variant="body2" sx={{ color: '#202223', fontWeight: 500 }}>
                                {review.seller_id || '-'}
                              </Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Rating value={review.rating} readOnly size="small" />
                              </Box>

                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                {formatDate(review.created_date)}
                              </Typography>

                              <Box>{renderStatus(review.status)}</Box>

                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {availableStatuses.map((statusOption) => (
                                  <IconButton
                                    key={statusOption.value}
                                    size="small"
                                    onClick={() => handleQuickStatusUpdate(review, statusOption.value)}
                                    disabled={updatingStatus === reviewId}
                                    title={`Mark as ${statusOption.label}`}
                                    sx={{
                                      color: statusOption.color,
                                      '&:hover': { bgcolor: statusOption.color + '15' },
                                      border: `1px solid ${statusOption.color}30`,
                                      borderRadius: '6px',
                                      width: 32,
                                      height: 32,
                                    }}
                                  >
                                    {updatingStatus === reviewId ? <CircularProgress size={14} /> : React.cloneElement(statusOption.icon, { fontSize: 'small' })}
                                  </IconButton>
                                ))}
                              </Box>

                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => handleActionMenuOpen(e, reviewId)} 
                                  sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}
                                >
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </Card>

                {/* Pagination */}
                {totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handleChangePage}
                      sx={{
                        '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 },
                        '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' },
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              // FORM VIEW
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ color: '#202223', textTransform: 'none', fontWeight: 500, mb: 2, '&:hover': { bgcolor: 'transparent' } }}
                  >
                    Reviews
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit review' : 'Add review'}
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Review details
                            </Typography>

                            {!isEditing && (
                              <>
                                <TextField
                                  name="product_id"
                                  label="Product ID *"
                                  type="number"
                                  value={formData.product_id || ''}
                                  onChange={handleInputChange}
                                  error={!!formErrors.product_id}
                                  helperText={formErrors.product_id}
                                  fullWidth
                                  required
                                  placeholder="Enter product ID"
                                  disabled={isFormSubmitting}
                                  sx={{ mb: 2 }}
                                  InputProps={{ inputProps: { min: 1 } }}
                                />

                                <TextField
                                  name="seller_id"
                                  label="Seller ID (Optional – for seller reviews)"
                                  type="number"
                                  value={formData.seller_id || ''}
                                  onChange={handleInputChange}
                                  fullWidth
                                  placeholder="Enter seller ID (leave empty for product review)"
                                  disabled={isFormSubmitting}
                                  sx={{ mb: 2 }}
                                  InputProps={{ inputProps: { min: 1 } }}
                                  helperText={
                                    formData.seller_id
                                      ? '📦 This will be saved as a Seller Review (type = 1)'
                                      : '🛍️ This will be saved as a Product Review (type = 0)'
                                  }
                                />

                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#202223' }}>Rating *</Typography>
                                  <Rating
                                    name="rating"
                                    value={formData.rating}
                                    onChange={handleRatingChange}
                                    precision={1}
                                    size="large"
                                    disabled={isFormSubmitting}
                                  />
                                  {formErrors.rating && <FormHelperText error>{formErrors.rating}</FormHelperText>}
                                </Box>

                                <TextField
                                  name="comment"
                                  label="Comment *"
                                  value={formData.comment || ''}
                                  onChange={handleInputChange}
                                  error={!!formErrors.comment}
                                  helperText={formErrors.comment}
                                  fullWidth
                                  required
                                  multiline
                                  rows={4}
                                  placeholder="Enter your review comment"
                                  disabled={isFormSubmitting}
                                />
                              </>
                            )}

                            {isEditing && editingReview && (
                              <Box sx={{ p: 2, bgcolor: '#fafbfb', borderRadius: '8px', border: '1px solid #e1e3e5' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>
                                    Review ID: {editingReview.id}
                                  </Typography>
                                  {renderReviewType(editingReview)}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                  <Avatar sx={{ width: 48, height: 48, bgcolor: '#f1f2f4' }}>
                                    {(editingReview.customer_name || 'A')[0].toUpperCase()}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                      {editingReview.customer_name || 'Anonymous'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Product ID: {editingReview.product_id}
                                      {editingReview.seller_id && ` | Seller ID: ${editingReview.seller_id}`}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Rating value={editingReview.rating} readOnly size="small" />
                                  <Typography variant="body2" color="text.secondary">({editingReview.rating})</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <CommentIcon sx={{ fontSize: 14, color: '#6d7175' }} />
                                  <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 500 }}>
                                    Comment:
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 2.5 }}>
                                  {editingReview.comment}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                        <Box sx={{ p: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>Status</Typography>
                          <FormControl fullWidth disabled={isFormSubmitting}>
                            <InputLabel>Status</InputLabel>
                            <Select value={formData.status?.toString() || '0'} onChange={handleStatusChange} label="Status">
                              <MenuItem value={0}><Box sx={{ display: 'flex', alignItems: 'center' }}><Pending sx={{ mr: 1, color: '#e65100' }} fontSize="small" />Pending</Box></MenuItem>
                              <MenuItem value={1}><Box sx={{ display: 'flex', alignItems: 'center' }}><Check sx={{ mr: 1, color: '#00783e' }} fontSize="small" />Approved</Box></MenuItem>
                              <MenuItem value={2}><Box sx={{ display: 'flex', alignItems: 'center' }}><Block sx={{ mr: 1, color: '#c62828' }} fontSize="small" />Rejected</Box></MenuItem>
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {formData.status === 1 ? 'Review will be visible to users' : formData.status === 2 ? 'Review will be marked as rejected' : 'Review will be pending approval'}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3, pt: 3, borderTop: '1px solid #e1e3e5' }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#f44336', color: '#eeeeee', borderRadius: '8px', px: 2.5 }}
                    >
                      Close
                    </Button>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#F78E12', color: '#eeeeee', borderRadius: '8px', px: 2.5 }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isFormSubmitting}
                      sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#006e52' } }}
                    >
                      {isFormSubmitting
                        ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={20} color="inherit" />{isEditing ? 'Updating...' : 'Adding...'}</Box>
                        : isEditing ? 'Update review' : 'Add review'}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Action context menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 140 } }}
      >
        <MenuItem onClick={() => { 
          if (selectedReviewId) { 
            const review = reviews.find(r => r.id === selectedReviewId); 
            if (review) handleEditReview(review); 
          } 
          handleActionMenuClose(); 
        }}>
          <Edit sx={{ mr: 1 }} fontSize="small" />Edit
        </MenuItem>
        <MenuItem onClick={() => { 
          if (selectedReviewId) handleDelete(selectedReviewId); 
          handleActionMenuClose(); 
        }} sx={{ color: '#d32f2f' }}>
          <Delete sx={{ mr: 1 }} fontSize="small" />Delete
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewManagement;